/**
 * CacheBustButton — Fixed bottom-right floating button for cache invalidation.
 *
 * Visible on every authenticated tenant panel page (mounted inside AppShell).
 * On click:
 *   1. POST /api/widget-settings/cache-bust → backend sets tenant.cache_bust_at = now
 *   2. For 5 minutes, all widget endpoints (/embed/widget.js, /widget/config,
 *      /widget/product) return `Cache-Control: no-store` → browser/CDN skip
 *      their cached copies, visitors get fresh content on next request.
 *   3. After the 5-min window, normal cache (60s widget.js / 15min config / 60s
 *      product) resumes so merchant site traffic doesn't hammer the backend.
 *
 * Browser cache ancak kullanıcının bir sonraki isteğinde refresh olur — bu buton
 * aktif oturumlardaki widget'ları remote olarak yenilemez; `no-store` ile sonraki
 * tüm GET'ler cache'siz backend'den gelir, böylece merchant Ctrl+Shift+R (veya
 * normal F5) ile anında yeni içeriği görür.
 */
import { useEffect, useState } from "react";
import { Eraser, Check, Loader2 } from "lucide-react";
import { toast } from "sonner";
import { api } from "@/lib/api";
import { useAuth } from "@/lib/auth";
import {
    Tooltip, TooltipContent, TooltipProvider, TooltipTrigger,
} from "@/components/ui/tooltip";

const WINDOW_SEC = 300; // backend ile aynı değer (routes/widget_settings.py)

export default function CacheBustButton() {
    const { session } = useAuth();
    const [busy, setBusy] = useState(false);
    const [bumpedAt, setBumpedAt] = useState(null);
    const [remaining, setRemaining] = useState(0);

    // Butonu sadece authenticated tenant kullanıcılarına göster.
    const authed = session && session !== false && session.user;

    // Son bump timestamp'ini sayaç olarak göster (5dk pencere kapanana kadar).
    useEffect(() => {
        if (!bumpedAt) return undefined;
        const tick = () => {
            const elapsed = Math.floor((Date.now() - bumpedAt) / 1000);
            const left = Math.max(0, WINDOW_SEC - elapsed);
            setRemaining(left);
            if (left === 0) setBumpedAt(null);
        };
        tick();
        const id = setInterval(tick, 1000);
        return () => clearInterval(id);
    }, [bumpedAt]);

    const onClick = async () => {
        if (busy) return;
        setBusy(true);
        try {
            await api.post("/widget-settings/cache-bust");
            setBumpedAt(Date.now());
            toast.success(
                "Önbellek temizlendi. 5 dakika boyunca ziyaretçiler her istekte taze içerik alacak. Sitenizi yenileyin.",
                { duration: 6000 },
            );
        } catch (e) {
            toast.error(e?.response?.data?.detail || "Önbellek temizlenemedi");
        } finally {
            setBusy(false);
        }
    };

    if (!authed) return null;

    const active = remaining > 0;
    const minsLeft = Math.ceil(remaining / 60);

    return (
        <TooltipProvider delayDuration={200}>
            <Tooltip>
                <TooltipTrigger asChild>
                    <button
                        type="button"
                        onClick={onClick}
                        disabled={busy || active}
                        data-testid="cache-bust-btn"
                        aria-label="Önbelleği temizle"
                        className={
                            "fixed bottom-6 right-6 z-50 h-12 min-w-12 rounded-full " +
                            "shadow-lg border transition-all duration-200 " +
                            "flex items-center gap-2 px-4 font-medium text-[12.5px] " +
                            (active
                                // Matte sage — aktif pencere boyunca butonun
                                // "iş tamam, cache atlanıyor" sinyali verir.
                                // disabled olduğu için hover state'i gereksiz.
                                ? "bg-success text-white border-success cursor-not-allowed opacity-95"
                                : "bg-foreground text-background border-foreground hover:bg-foreground/90") +
                            (busy ? " opacity-70 cursor-wait" : (active ? "" : " hover:shadow-xl active:scale-95"))
                        }
                    >
                        {busy ? (
                            <Loader2 className="h-4 w-4 animate-spin shrink-0" strokeWidth={2} />
                        ) : active ? (
                            <Check className="h-4 w-4 shrink-0" strokeWidth={2.25} />
                        ) : (
                            <Eraser className="h-4 w-4 shrink-0" strokeWidth={2} />
                        )}
                        <span className="whitespace-nowrap">
                            {busy
                                ? "Temizleniyor…"
                                : active
                                    ? `Aktif · ${minsLeft} dk`
                                    : "Önbelleği Temizle"}
                        </span>
                    </button>
                </TooltipTrigger>
                <TooltipContent side="left" className="max-w-sm">
                    <p className="text-[11.5px] leading-relaxed">
                        {active ? (
                            <>
                                <strong>Önbellek atlama penceresi aktif.</strong>{" "}
                                Önümüzdeki {minsLeft} dakika boyunca widget tarayıcı ve CDN
                                cache'leri atlanır — her ziyaretçi her istekte taze içerik alır.
                                Pencere sonunda normal cache (60 sn / 15 dk) devreye girer.
                            </>
                        ) : (
                            <>
                                Beden tablosu / görsel / model / kalıp atamaları ve panel
                                aktif-pasif ayarları ziyaretçi tarayıcılarında cache'lenir.
                                Değişiklikleri hemen görmek için bu butonu kullan: 5 dakika
                                boyunca cache atlanır.
                            </>
                        )}
                    </p>
                </TooltipContent>
            </Tooltip>
        </TooltipProvider>
    );
}
